import { text } from "@theme/fetch";
import {
  deferImage,
  loadDeferredImages,
  morph,
  parseHtml,
} from "@theme/helpers";
import { HTMLCustomElement } from "@theme/html-custom-element";
import { Swiper } from "@theme/swiper";

const ACTIVE_CLASS = "is-active";
const TAB_FADE_MS = 300;

const SWIPER_OPTIONS = {
  slidesPerView: 1.5,
  spaceBetween: 16,
  freeMode: true,
  centerInsufficientSlides: true,
  mousewheel: {
    enabled: true,
    forceToAxis: true,
    releaseOnEdges: true,
  },
  on: {
    sliderFirstMove: (swiper) => swiper.el.classList.add("is-dragging"),
    touchEnd: (swiper) => swiper.el.classList.remove("is-dragging"),
  },
  breakpoints: {
    768: {
      slidesPerView: 2.5,
      spaceBetween: 16,
    },
    1024: {
      slidesPerView: 4.5,
    },
  },
};

class ProductRecommendations extends HTMLCustomElement {
  requiredAttributes = ["url"];
  /** @type {WeakMap<Element, import("swiper").Swiper>} */
  #swiperByEl = new WeakMap();

  connectedCallback() {
    text(fetch(`${this.getAttribute("url")}`), async (response) => {
      /**
       * @see https://shopify.dev/docs/api/ajax/reference/product-recommendations#error-responses
       */

      if (response.status === 404) {
        throw new Error("Product or section not found");
      }

      if (response.status === 422) {
        throw new Error("A parameter was invalid");
      }

      return response;
    }).then(async (response) => {
      const html = parseHtml(response, "product-recommendations");
      const [, ...hiddenTabs] = html.querySelectorAll(
        ".product-feed-slider__tab",
      );

      for (const img of hiddenTabs.flatMap((tab) => [
        ...tab.querySelectorAll("img"),
      ])) {
        deferImage(img);
      }

      await morph(this, html);
      this.initSwipers();
      this.initTabs();
    });
  }

  initSwipers() {
    for (const swiperEl of this.querySelectorAll(
      ".product-feed-slider__tab-products.swiper",
    )) {
      if (this.#swiperByEl.has(swiperEl)) {
        continue;
      }

      swiperEl.setAttribute("data-lenis-prevent-horizontal", "");
      this.#swiperByEl.set(swiperEl, new Swiper(swiperEl, SWIPER_OPTIONS));
    }
  }

  initTabs() {
    const buttonsContainer = this.querySelector(
      ".product-feed-slider__buttons",
    );
    this.tabs = [...this.querySelectorAll(".product-feed-slider__tab")];
    const buttons = [...this.querySelectorAll(".product-feed-slider__button")];

    if (!buttonsContainer || !this.tabs.length) {
      return;
    }

    for (const button of buttons) {
      buttonsContainer.appendChild(button);

      for (const type of ["pointerenter", "focus"]) {
        button.addEventListener(
          type,
          () =>
            loadDeferredImages(
              this.tabs.find((tab) => tab.dataset.tabId === button.dataset.tabId),
            ),
          { once: true },
        );
      }

      button.addEventListener("click", () =>
        this.activateTab(button.dataset.tabId),
      );
    }

    const firstTab = this.tabs[0];
    if (firstTab) {
      this.activateTab(firstTab.dataset.tabId);
    }
  }

  activateTab(tabId) {
    if (!tabId) {
      return;
    }

    let nextTab = null;
    for (const tab of this.tabs) {
      const isActive = tab.dataset.tabId === tabId;
      tab.classList.toggle(ACTIVE_CLASS, isActive);
      if (isActive) {
        nextTab = tab;
      }
    }

    for (const button of this.querySelectorAll(
      ".product-feed-slider__button",
    )) {
      const isActive = button.dataset.tabId === tabId;
      button.classList.toggle(ACTIVE_CLASS, isActive);
      button.setAttribute("aria-pressed", `${isActive}`);
    }

    this.syncCollectionCta(nextTab);

    if (nextTab) {
      loadDeferredImages(nextTab);
      this.refreshSwiperForTab(nextTab);
    }
  }

  /**
   * Shop all CTA follows the active collection/vendor tab; fades out on related.
   * @param {Element | null} tab
   */
  syncCollectionCta(tab) {
    const url = tab?.getAttribute("data-collection-url") || "";
    const hasUrl = Boolean(url);
    const ctas = this.querySelectorAll(
      ".product-recommendations__collection-cta",
    );
    const mobileContainer = this.querySelector(
      ".product-feed-slider__button-container",
    );

    for (const cta of ctas) {
      if (!(cta instanceof HTMLAnchorElement)) {
        continue;
      }

      if (hasUrl) {
        cta.href = url;
        cta.removeAttribute("tabindex");
      } else {
        cta.tabIndex = -1;
      }

      cta.classList.toggle("is-visible", hasUrl);
      cta.setAttribute("aria-hidden", hasUrl ? "false" : "true");
    }

    if (mobileContainer) {
      mobileContainer.classList.toggle("is-visible", hasUrl);
    }
  }

  /**
   * Swiper measures while the tab is hidden (opacity 0); refresh after the fade so widths are correct.
   * @param {Element} tab
   */
  refreshSwiperForTab(tab) {
    const swiperEl = tab.querySelector(".swiper");
    const swiper = swiperEl ? this.#swiperByEl.get(swiperEl) : undefined;

    if (!swiper) {
      return;
    }

    requestAnimationFrame(() => swiper.update());
    window.setTimeout(() => swiper.update(), TAB_FADE_MS);
  }
}

if (!customElements.get("product-recommendations"))
  customElements.define("product-recommendations", ProductRecommendations);
